import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, NgZone, OnChanges, OnDestroy, OnInit, PLATFORM_ID, Renderer2, SimpleChanges, ViewEncapsulation, computed, output, input, model, signal, inject, DOCUMENT } from '@angular/core';
import {isPlatformBrowser, isPlatformServer} from "@angular/common";
import { EditorType } from '@toast-ui/editor';

let Editor: any;
const waitingForLibraryLoad = globalThis['process']
  ? async () => Promise.resolve()
  : async () => (import('@toast-ui/editor')).then((e: any) => { loadCss(); Editor = e.Editor; });

const loadCss = () => {
  if (Editor) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'toastui-editor.css';
  document.head.appendChild(link);
}

export interface ITuiCustomButton {
  /** Button caption */
  text: string;
  /** Text inserted at the cursor on click */
  insert: string;
  tooltip?: string;
}

export const TUI_TOOLBAR = [
  ['heading', 'bold', 'italic', 'strike'],
  ['hr', 'quote'],
  ['ul', 'ol', 'task', 'indent', 'outdent'],
  ['table', 'image', 'link'],
  ['code', 'codeblock']
];

@Component({
  selector: 'ui-tui-editor',
  template: '',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./toastui-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.height.px]': 'currentHeight()',
    '[class.resizable]': 'resizable()',
    '[class.ng-touched]': 'isInPortal',
    '[class.ng-invalid]': 'isInvalidInput'
  }
})
export class ToastUIEditorComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
  private el = inject(ElementRef);
  private r2 = inject(Renderer2);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject<Object>(PLATFORM_ID);

  readonly placeholder = input('');
  readonly value = model<string>('');
  readonly disabled = input(false);
  readonly autofocus = input(false);
  readonly uploadEndpoint = input('');
  readonly canUploadImages = input(false);
  readonly height = input(300);
  /** Shows a drag handle at the bottom edge, letting the user grow the editor below `height` */
  readonly resizable = input(false);
  readonly initialEditType = input<EditorType>('markdown');
  readonly maxLength = input(0);
  readonly btnFullscreen = input(true);
  /** Extra toolbar buttons inserting a predefined text, added as a separate group */
  readonly customButtons = input<ITuiCustomButton[]>([]);
  readonly valueChange = output<string>();
  readonly change = output<string>();
  editor: any;
  isUploading = false;
  isTouched = false;
  isInvalid = false;
  private isFullscreen = false;
  private preventOnChangeTrigger = false;
  private updatingMaxlength = false;
  // Anchor marking the host's original DOM position while it is teleported to body in fullscreen
  private fsAnchor: Comment | null = null;
  /** Height picked by dragging the resize handle, `null` until the user resizes */
  private readonly resizedHeight = signal<number | null>(null);
  protected readonly currentHeight = computed(() => this.resizedHeight() ?? this.height());
  // Tears down an in-progress resize drag
  private stopResize: (() => void) | null = null;

  protected get isInPortal() {
    return this.isTouched;
  }

  protected get isInvalidInput() {
    return this.isInvalid;
  }

  ngOnInit() {
    this.updateValidState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.editor) {
      return;
    }
    if (changes['value'].currentValue !== this.editor.getMarkdown()) {
      this.preventOnChangeTrigger = true;
      this.editor.setMarkdown(changes['value'].currentValue, false);
      this.preventOnChangeTrigger = false;
      this.updateValidState();
    }
  }

  ngOnDestroy() {
    // Restore the host to its original position if destroyed while fullscreen, so the
    // teleported node isn't orphaned in body and the scroll lock is released
    if (this.isFullscreen) {
      this.exitFullscreen();
    }
    this.stopResize?.();
  }

  private enterFullscreen() {
    const el = this.el.nativeElement as HTMLElement;
    // Teleport to body so fixed positioning escapes the portal-page stacking context (view-transition-name)
    this.fsAnchor = this.document.createComment('tui-fullscreen-anchor');
    el.parentNode?.insertBefore(this.fsAnchor, el);
    this.document.body.appendChild(el);
    this.document.body.style.overflow = 'hidden';
    el.classList.add('fullscreen');
    this.isFullscreen = true;
  }

  private exitFullscreen() {
    const el = this.el.nativeElement as HTMLElement;
    el.classList.remove('fullscreen');
    this.document.body.style.removeProperty('overflow');
    if (this.fsAnchor?.parentNode) {
      this.fsAnchor.parentNode.insertBefore(el, this.fsAnchor);
      this.fsAnchor.parentNode.removeChild(this.fsAnchor);
    }
    this.fsAnchor = null;
    this.isFullscreen = false;
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      void this.createEditor();
    });
  }

  updateValidState() {
    let text = this.value();
    if (this.editor) {
      text = this.editor.getMarkdown();
    }
    const required = !!this.el?.nativeElement?.attributes.getNamedItem('required');
    this.isInvalid = required && !text;
  }

  /**
   * Creates editor
   * @private
   */
  private async createEditor() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    await waitingForLibraryLoad();

    let toolbar = [...TUI_TOOLBAR];
    if (this.btnFullscreen()) {
      const fullscreen = [{
        el: this.createFullscreenButton(),
        tooltip: 'Fullscreen'
      }];
      toolbar = [fullscreen as any, ...toolbar];
    }

    const customButtons = this.customButtons();
    if (customButtons.length) {
      toolbar = [...toolbar, customButtons.map(b => ({
        name: b.text,
        tooltip: b.tooltip ?? b.text,
        el: this.createCustomButton(b)
      })) as any];
    }

    this.editor = new Editor({
      autofocus: this.autofocus(),
      el: this.el.nativeElement,
      previewStyle: 'vertical',
      height: this.height() + 'px',
      initialValue: this.value(),
      initialEditType: this.initialEditType(),
      placeholder: this.placeholder(),
      toolbarItems: toolbar,
      /* softbreak: '\n',
      customHTMLRenderer: {
        softbreak(_, { options }) {
          return {
            type: 'html',
            content: options.softbreak
          };
        }
      }, */
      events: {
        change: () => {
          this.isTouched = true;
          this.updateValidState();
          this.applyMaxLength();

          if (!this.preventOnChangeTrigger) {
            const txt = this.editor.getMarkdown();
            this.valueChange.emit(txt);
            this.change.emit(txt);
          }
        }
      },
      hooks: {
        addImageBlobHook: async (blob: any, callback: (url: string, alt: string) => void, action: string) => {
          if (!this.canUploadImages()) {
            return false;
          }
          const url = await this.uploadImage(blob);
          if (url) {
            callback(url, 'Image');
          }
          return false;
        }
      }
    });
    this.fixLinkPopup();
    if (this.resizable()) {
      this.createResizeHandle();
    }
    if (this.disabled()) {
      // TODO: find better way to apply disabled state
      this.editor.mdEditor.el.parentElement.parentElement.parentElement.parentElement.classList.add('disabled');
    }
  }

  /**
   * TUI never pre-fills URL of existing link in the link popup (nhn/tui.editor#1256, repo archived).
   * Replaces built-in popup query and wysiwyg "addLink" command, so existing link URL and text can be edited
   * @private
   */
  private fixLinkPopup() {
    const em = this.editor.eventEmitter;
    em.removeEventHandler('query');
    em.listen('query', (query: string, payload: any) =>
      query === 'getPopupInitialValues' && payload.popupName === 'link' ? { linkText: this.selectLink() } : {});
    // Built-in command ignores changed link text when text is selected
    this.editor.addCommand('wysiwyg', 'addLink', ({ linkUrl, linkText }: any, state: any, dispatch: any) => {
      const { from, to } = state.selection;
      const mark = state.schema.marks.link.create({ linkUrl });
      const tr = state.tr;
      // Empty text comes from image link popup
      if (!linkText || linkText === state.doc.textBetween(from, to)) {
        tr.addMark(from, to, mark);
      } else {
        tr.replaceRangeWith(from, to, state.schema.text(linkText, [mark]));
      }
      dispatch(tr.scrollIntoView());
      // Collapse selection, so the applied link is visible
      const end = tr.mapping.map(to);
      this.editor.setSelection(end, end);
    });
  }

  /**
   * If cursor is on a link, selects the whole link and pre-fills its URL in the popup. Returns selected text
   * @private
   */
  private selectLink(): string {
    if (!this.editor.isWysiwygMode()) {
      return this.editor.getSelectedText();
    }
    const { selection, schema } = this.editor.wwEditor.view.state;
    const $pos = selection.$from;
    const mark = schema.marks.link.isInSet($pos.nodeAfter?.marks ?? []);
    if (mark) {
      // Expand to adjacent text nodes with the same link (e.g. partially bold link)
      const parent = $pos.parent;
      let start = $pos.index();
      let end = start + 1;
      while (start > 0 && mark.isInSet(parent.child(start - 1).marks)) start--;
      while (end < parent.childCount && mark.isInSet(parent.child(end).marks)) end++;
      this.editor.setSelection($pos.posAtIndex(start), $pos.posAtIndex(end));
      // Popup resets URL on render, so fill it after. URL isn't passed as initial value, because then TUI locks "Link text"
      queueMicrotask(() => this.el.nativeElement.querySelector('#toastuiLinkUrlInput').value = mark.attrs.linkUrl);
    }
    return this.editor.getSelectedText();
  }

  private applyMaxLength() {
    const maxLength = this.maxLength();
    if (maxLength === 0 || this.updatingMaxlength) {
      return;
    }
    const content = this.editor.getMarkdown();
    if (content.length > maxLength) {
      this.updatingMaxlength = true;
      this.editor.setMarkdown(content.substring(0, maxLength), false);
      this.editor.moveCursorToEnd(false);
      this.updatingMaxlength = false;
    }
  }
  /**
   * Add fullscreen button to toolbar
   * @private
   */
  private createFullscreenButton(): HTMLButtonElement {
    const button = this.r2.createElement('button');
    const _this = this;
    button.className = 'first btn-fullscreen';
    button.style.backgroundImage = 'none';
    button.style.margin = '0';
    button.innerHTML = ``;
    if (isPlatformBrowser(this.platformId)) {
      button.addEventListener('click', () => {
        if (_this.isFullscreen) {
          _this.exitFullscreen();
        } else {
          _this.enterFullscreen();
        }
      });
    }

    return button;
  }

  /**
   * Adds a drag handle to the bottom edge, so the editor can be stretched like a native textarea.
   * A handle is used instead of CSS `resize`, because that needs `overflow` other than `visible` on the host,
   * which clips the editor's own popups (link, table, heading) when they are taller than the editor
   * @private
   */
  private createResizeHandle() {
    const handle = this.r2.createElement('div') as HTMLElement;
    handle.className = 'tui-resize-handle';
    handle.addEventListener('mousedown', e => this.onResizeStart(e));
    this.el.nativeElement.appendChild(handle);
  }

  /**
   * Resizes the host while the handle is dragged. Runs outside Angular writing the style directly,
   * so dragging doesn't trigger change detection on every move, and commits the result on release
   * @private
   */
  private onResizeStart(e: MouseEvent) {
    e.preventDefault();
    const host = this.el.nativeElement as HTMLElement;
    const startY = e.clientY;
    const startHeight = host.getBoundingClientRect().height;
    let height = startHeight;

    const onMove = (ev: MouseEvent) => {
      height = Math.max(this.height(), startHeight + ev.clientY - startY);
      host.style.height = `${height}px`;
    };
    const onUp = () => {
      this.stopResize = null;
      this.document.removeEventListener('mousemove', onMove);
      this.document.removeEventListener('mouseup', onUp);
      this.document.body.style.removeProperty('user-select');
      // Commit, so the host binding keeps the dragged height instead of resetting it on the next check
      this.zone.run(() => this.resizedHeight.set(height));
    };
    this.stopResize = onUp;

    this.document.addEventListener('mousemove', onMove);
    this.document.addEventListener('mouseup', onUp);
    // Dragging over the editor would otherwise select its text
    this.document.body.style.userSelect = 'none';
  }

  /**
   * Creates toolbar button which inserts predefined text into the editor
   * @private
   */
  private createCustomButton(btn: ITuiCustomButton): HTMLButtonElement {
    const button = this.r2.createElement('button') as HTMLButtonElement;
    button.type = 'button';
    button.className = 'btn-tui-custom';
    button.textContent = btn.text;
    if (isPlatformBrowser(this.platformId)) {
      button.addEventListener('click', () => this.editor?.insertText(btn.insert));
    }

    return button;
  }

  private async uploadImage(blob: any): Promise<string> {
    return new Promise<string>((res, rej) => {
      const formData = new FormData();
      formData.append('image', blob);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.uploadEndpoint() || ToastUIEditorComponent.defaultUploadEndpoint, true);
      xhr.upload.onloadend = _ => {
        this.isUploading = false;
      };
      xhr.upload.onerror = _ => {
        this.isUploading = false;
      };
      xhr.onreadystatechange = () => {
        if (xhr.readyState == 4) {
          this.isUploading = false;
          let url = '';
          try {
            const obj = JSON.parse(xhr.responseText);
            if (obj.errors && obj.errors.length) {
              console.error(obj);
              return;
            }
            url = location.origin + JSON.parse(xhr.responseText).url;
          } catch (e) {
            console.error(e);
          }
          res(url);
        }
      };
      xhr.send(formData);
    });
  }

  detectChanges() {
    this.cdr.detectChanges();
  }

  static changeToolbar(...items: any[]) {
    TUI_TOOLBAR.splice(0, TUI_TOOLBAR.length);
    TUI_TOOLBAR.push(...items);
  }

  static defaultUploadEndpoint = '';
}
